import * as Styled from './KPIWidget.styles';
import { IKPIWidget } from './KPIWidget.types';
import { TimeUnits, Unit, PowerUnits } from './KPIWidget.types';
import { GoDotFill } from 'react-icons/go';
import CountUp from 'react-countup';
import WidgetContainer from './../../Atoms/WidgetContainer/WidgetContainer';
import KPIWidgetSkeleton from './KPIWidget.skeleton';
import AnimatedTimeDisplay from './../../Atoms/AnimatedTimeDisplay/AnimatedTimeDisplay';
import { convertPower, convertTime, formatTimeString } from './KPIWidget.utils';
import { useTheme } from '@mui/material';

const KPIValue = ({ value, unit, animated }: { value: number; unit: Unit; animated: boolean }) => {
  const decimalPlaces = (value?.toString().split('.')[1] || '').length;

  switch (unit) {
    case TimeUnits.S:
    case TimeUnits.MIN:
    case TimeUnits.H:
    case TimeUnits.D:
    case TimeUnits.MO:
    case TimeUnits.W:
    case TimeUnits.Y:
      if (animated) {
        return <AnimatedTimeDisplay timeObject={convertTime(value, unit)} />;
      } else {
        return <>{formatTimeString(convertTime(value, unit))}</>;
      }
    case PowerUnits.WH:
    case PowerUnits.KWH:
    case PowerUnits.MWH:
      return (
        <>
          <Styled.KPIValue variant='h3'>
            {animated && value && (
              <CountUp decimals={decimalPlaces} preserveValue end={convertPower(value, unit).value} duration={0.5} />
            )}
            {!animated && <>{convertPower(value, unit).value}</>}
          </Styled.KPIValue>
          <Styled.KPIUnit variant='h5'>{convertPower(value, unit)?.unit}</Styled.KPIUnit>
        </>
      );
    default:
      return (
        <>
          <Styled.KPIValue variant='h3'>
            {animated && value && <CountUp decimals={decimalPlaces} preserveValue end={value} duration={0.5} />}
            {!animated && <>{value}</>}
          </Styled.KPIValue>
          <Styled.KPIUnit variant='h5'>{unit}</Styled.KPIUnit>
        </>
      );
  }
};

const KPIWidget = (props: IKPIWidget) => {
  const theme = useTheme();
  if (props.value !== undefined && props?.unit !== undefined) {
    const trendUp = props.value >= (props.compareValue || 0);
    const compareValue = props.compareValue;
    const compareValueDescription = `Previous period: ${compareValue} ${props.unit}`;
    const percentage = calculatePercentage(props.value, compareValue || 0);

    return (
      <WidgetContainer
        title={props.title}
        infoMessage={props.infoMessage}
        maxWidth={600}
        isLoading={props.isLoading}
        error={props.error}
      >
        <Styled.KPIColumn display={props.compare}>
          <Styled.KPIRow>
            <KPIValue unit={props?.unit} value={props.value} animated={props.animated} />
          </Styled.KPIRow>

          <Styled.KPIChip display={props.compare}>
            {trendUp && (
              <Styled.KPIChipUp
                title={compareValueDescription}
                color='default'
                icon={<GoDotFill size={20} color={theme.palette.success.main} />}
                label={`+${percentage}%`}
              />
            )}

            {!trendUp && (
              <Styled.KPIChipDown
                title={compareValueDescription}
                color='default'
                icon={<GoDotFill size={20} color={theme.palette.error.main} />}
                label={`${percentage}%`}
              />
            )}
          </Styled.KPIChip>
        </Styled.KPIColumn>
      </WidgetContainer>
    );
  } else {
    return <KPIWidgetSkeleton previous error={props.error} />;
  }
};

function calculatePercentage(newValue: number, previousValue: number): number {
  if (previousValue === 0 && newValue !== 0) {
    return 100;
  }
  if (newValue === 0 && previousValue === 0) {
    return 0;
  }
  if (newValue === 0 && previousValue !== 0) {
    return -100;
  }
  const difference = newValue - previousValue;
  const percentage = (difference / previousValue) * 100;

  return Math.round(percentage);
}

export default KPIWidget;
